import { usePollingInterval } from 'src/hooks/usePollingInterval'
import type { Theme } from '@material-ui/core'
import { Card, Grid, makeStyles } from '@material-ui/core'
import type { LeaderboardRankResponse, QuoteSummaryResponse, UnreadMessagesQuery } from '@paintscout/api'
import {
  UnreadMessagesDocument,
  useLeaderboardQuery,
  useQuoteSummaryQuery,
  useReadMessagesMutation,
  useSearchLocationsQuery,
  useSearchQuotesQuery,
  useUnreadMessagesQuery
} from '@paintscout/api'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { formatCurrency, getFeature, getObjectLabels } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { getRangePresetValue } from '@paintscout/util/util'
import {
  useIsMobile,
  Button,
  DateRangePicker,
  DropdownSelect,
  EstimatorSelect,
  Hidden,
  Spinner,
  StatCardGamified,
  StatCardMainGamified,
  StatCardProgressBar,
  StatCardProgressGamified,
  Typography,
  useClientOptions,
  useDialogs,
  useUser,
  GoogleMapsComponent
} from '@paintscout/ui'
import type { StyleClasses, DropdownSelectOption } from '@paintscout/ui'
import { jsonToLucene } from 'json-to-lucene'
import moment from 'moment'
import type { Gamification } from 'paintscout'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useConnection } from 'react-detect-offline'
import DocumentTitle from 'src/components/common/DocumentTitle'
import PageTitle from 'src/components/common/PageTitle'
import QuoteTable from 'src/components/common/QuoteTable'
import Role from 'src/components/common/Role'
import GamificationLaunchDialog from 'src/components/dialogs/GamificationLaunchDialog'
import BasePage from 'src/components/layouts/BasePage'
import { ChatActivityComponent } from '../Dashboard/ChatActivityComponent'
import DashboardActivity from '../Dashboard/DashboardActivity'
import { CorporateQuoteTypeSelect, QuoteTypeSelect } from '@paintscout/react-quote'
import classNames from 'classnames'
import EventDocList from 'src/components/common/EventTable/EventDocList'
import TaskDocList from 'src/components/common/TaskTable/TaskDocList'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface DashboardProps {
  classes?: StyleClasses<typeof useStyles>
}

const useStyles = makeStyles<Theme, DashboardProps>(
  (theme) => ({
    root: {},
    crewWrapper: {
      overflow: 'visible',
      minHeight: 'calc(100vh - 58px)'
    },
    filterContainer: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
      paddingRight: 0,
      paddingLeft: theme.spacing(2),
      borderRadius: theme.borderRadius.xl,
      overflow: 'visible'
    },
    dropdownSelect: {
      '& .select__value-container': {
        minHeight: 60,
        [theme.breakpoints.down('xs')]: {
          minHeight: 30
        }
      }
    },
    archivedCheckbox: {
      marginTop: 16,
      [theme.breakpoints.down('xs')]: {
        marginTop: 0
      }
    },
    activityTabs: {
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2)
    },
    activityTab: {
      ...theme.typography.subtitle2,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      color: theme.palette.text.secondary,
      textTransform: 'none',
      border: `1px solid transparent`,
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      borderRadius: theme.borderRadius.lg,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
        border: `1px solid ${theme.paintscout.palette.primary}`
      }
    },
    tabButtonContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      width: '100%',
      alignItems: 'center',
      gap: theme.spacing(2),
      maxWidth: '100%',
      overflow: 'hidden',
      overflowX: 'auto',
      padding: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    tabButton: {
      ...theme.typography.subtitle2,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      color: theme.palette.text.primary,
      textTransform: 'none',
      border: `1px solid transparent`,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
        border: `1px solid ${theme.paintscout.palette.primary}`,
        boxShadow: 'none'
      }
    },
    activeTabButton: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      },
      '&:active': {
        backgroundColor: theme.palette.primary.main
      },
      '&:focus': {
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.paintscout.palette.primary}`,
        boxShadow: 'none'
      },
      '&:focus-visible': {
        backgroundColor: theme.palette.primary.main,

        border: `1px solid ${theme.paintscout.palette.primary}`,
        boxShadow: 'none'
      }
    },
    selectedTab: {
      color: theme.palette.primary.main
    },
    chatTab: {
      overflow: 'visible'
    },
    chatTabWrapper: {},
    chatTabBadge: {
      top: '0.5em',
      right: '-1.5em'
    },
    popover: {
      maxWidth: theme.typography.pxToRem(250),
      maxHeight: theme.typography.pxToRem(400),
      minWidth: theme.typography.pxToRem(250),
      minHeight: theme.typography.pxToRem(100)
    },
    popoverHeader: {
      display: 'flex',
      justifyContent: 'flex-end',
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      padding: theme.spacing(0.5),
      paddingRight: theme.spacing(1)
    },
    cachedOutlineIcon: {
      fontSize: theme.typography.pxToRem(16),
      verticalAlign: 'middle',
      marginLeft: 0,
      marginTop: -theme.spacing(0.5),
      '& > span > svg': {
        fontSize: '1.2rem',
        color: theme.palette.text.secondary,
        transform: 'scaleX(-1)'
      }
    },
    primaryTitle: {
      [theme.breakpoints.up('md')]: {
        fontSize: '2.5rem'
      }
    },
    roundedCard: {
      borderRadius: theme.borderRadius.xl
    }
  }),
  { name: 'Dashboard' }
)

const DEFAULT_REPORTS: QuoteSummaryResponse = {
  totalQuotes: 0,
  totalQuotePrice: 0,
  openQuotes: 0,
  openQuotePrice: 0,
  wonQuotes: 0,
  wonQuotePrice: 0,
  closedQuotes: 0,
  closedQuotePrice: 0,

  closeRate: 0,
  winRate: 0,
  dollarsWon: 0,

  averageClosePrice: 0,
  averageQuotePrice: 0,
  averageWonPrice: 0,
  averageOpenPrice: 0
}

const DEFAULT_LEADERBOARD_RANK: LeaderboardRankResponse = {
  totalSold: {
    value: 0,
    rank: 0,
    valueChange: 0,
    rankChange: 0,
    comment: ''
  },
  winRate: {
    value: 0,
    rank: 0,
    valueChange: 0,
    rankChange: 0,
    comment: ''
  }
}

export interface DashboardState {
  quoteDateMin: number
  quoteDateMax: number
  rangePresetKey: string
  estimators: { value: string; label: string }[]
  archived: boolean
  quoteTypes: string[]
  selectedLocations: DropdownSelectOption[]
}

function Dashboard(props: DashboardProps) {
  const classes = useStyles(props)
  const isXs = useIsMobile({ xs: true })
  const isLg = useIsMobile({ custom: 1280 })
  const { online } = useConnection()
  const { options, clientId } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { crm: crmFlag, productionFlag } = useFlags()
  const gamificationFeatures: Gamification = getFeature({ options, path: 'gamification' })
  const enableArchiveSearch = gamificationFeatures?.features?.enableArchiveSearch ?? false
  const useAcceptedDate = !options?.options?.quotes?.dontCountAcceptedDate
  const { profile, user, hasPermissions, isSales, isCrew, isAdmin, isCorporate, isMasquerading, highestRole } =
    useUser()
  const isCorporateDashboard = isCorporate && !isMasquerading
  const excludeFromLeaderboards = user.app_metadata?.company?.excludeFromLeaderboards
  const showGamifiedCallouts =
    !excludeFromLeaderboards && gamificationFeatures?.features?.dashboardStandings && (isAdmin || isSales)
  const canSeeEstimatorSelect = hasPermissions(['read:estimator-select']) && !isCorporateDashboard
  const [currentMobileTab, setCurrentMobileTab] = useState('activity')
  const [currentActivityTab, setCurrentActivityTab] = useState('activity')
  const [currentEventTab, setCurrentEventTab] = useState('chat')

  const lastUpdated = useRef(Date.now())
  const { openDialog } = useDialogs()

  const [dashboardState, setDashboardState] = useState<DashboardState>(() => {
    const savedStateLegacy = JSON.parse(window.localStorage.getItem(`dashboardOptions`))
    const savedStateNew = JSON.parse(window.localStorage.getItem(`dashboardOptions-${clientId}`)) // Now saving with clientId re: #15837
    const savedState = savedStateNew || savedStateLegacy
    if (!savedState) {
      return {
        quoteDateMin: moment().subtract(1, 'months').startOf('day').unix() * 1000,
        quoteDateMax: moment().endOf('day').unix() * 1000,
        rangePresetKey: 'lastThirtyDays',
        estimators: [],
        archived: undefined,
        quoteTypes: [],
        selectedLocations: []
      }
    }

    let quoteDateMin = savedState.quoteDateMin
    let quoteDateMax = savedState.quoteDateMax

    if (savedState.rangePresetKey) {
      const selected = getRangePresetValue(savedState.rangePresetKey)
      if (selected) {
        quoteDateMin = selected.from.unix() * 1000
        quoteDateMax = selected.to.unix() * 1000
      }
    }
    const cleanStateEstimators = (savedState?.estimators ?? []).filter((estimator) => typeof estimator === 'object')

    return {
      quoteTypes: [],
      selectedLocations: [],
      ...savedState,
      estimators: cleanStateEstimators,
      quoteDateMin,
      quoteDateMax
    }
  })

  /*
    If there is a selected rangePresetKey, we don't actually want to use the 
    stored quoteDateMin/quoteDateMax... But rather, we'll want to re-calculate
    it each time we render (so that we end up with the most current results)  
  */
  const { estimators, quoteTypes, archived, selectedLocations, quoteDateMin, quoteDateMax } = dashboardState
  const leaderboardId = isSales ? user.user_id : estimators?.[0]?.value ?? user.user_id
  const [allEstimators, setAllEstimators] = useState<DashboardEstimator[]>([])

  useEffect(() => {
    window.localStorage.setItem(`dashboardOptions-${clientId}`, JSON.stringify(dashboardState))
  }, [dashboardState])

  const quoteSummaryQuery = getQuoteSummaryQuery({
    estimators,
    quoteTypes,
    quoteDateMin,
    quoteDateMax,
    archived,
    selectedLocations,
    isCorporate,
    isMasquerading,
    useAcceptedDate
  })

  const activityQuery = getActivityQuery({
    estimators,
    quoteTypes,
    quoteDateMin,
    quoteDateMax
  })

  const chatQuery = getChatQuery({ estimators, quoteDateMax, quoteDateMin, quoteTypes })

  const { data: messagesData, refetch: _refetchMessages = () => {} } = useUnreadMessagesQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: usePollingInterval(120000),
    skip: isCorporate && !isMasquerading
  })

  const [readMessages] = useReadMessagesMutation({
    update: (cache, result) => {
      if (result?.data?.readMessages?.length > 0) {
        cache.writeQuery<UnreadMessagesQuery>({
          query: UnreadMessagesDocument,
          data: {
            __typename: 'Query',
            unreadMessages: []
          }
        })
      }
    }
  })

  const { loading: quoteSummaryFetching, data: quoteSummaryData } = useQuoteSummaryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    variables: { query: quoteSummaryQuery }
  })

  const { data: leaderboardData, loading: fetchingLeaderboard } = useLeaderboardQuery({
    skip: !showGamifiedCallouts || !leaderboardId || !gamificationFeatures.enabled,
    fetchPolicy: 'cache-and-network',
    variables: {
      userId: leaderboardId,
      sort: 'totalSold',
      searchTeam: false
    },
    notifyOnNetworkStatusChange: true,
    onError: useCallback((_error) => {}, []),
    onCompleted: useCallback((_data) => {
      setLeaderboardRank(_data?.leaderboardRank ?? DEFAULT_LEADERBOARD_RANK)
    }, [])
  })

  const loading = fetchingLeaderboard || (quoteSummaryFetching && !quoteSummaryData.quoteSummary)
  const [leaderboardRank, setLeaderboardRank] = useState<LeaderboardRankResponse>(
    leaderboardData?.leaderboardRank ?? DEFAULT_LEADERBOARD_RANK
  )

  const reports = quoteSummaryData?.quoteSummary ?? DEFAULT_REPORTS

  const {
    loading: locationsLoading,
    data: locationsData,
    error: _locationsError
  } = useSearchLocationsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    skip: !isCorporateDashboard,
    variables: {
      query: '',
      limit: 200
    }
  })
  const rows = locationsData?.searchLocations?.rows ?? []
  const locations = rows.map((row) => ({
    label: row.name,
    value: row._id
  }))

  const handleJoinClick = () => {
    openDialog(GamificationLaunchDialog, {
      isAdmin
    })
  }

  return (
    <BasePage className={highestRole === 'crew' ? classes.crewWrapper : undefined}>
      <DocumentTitle title={'Dashboard'} />
      {!isXs && <DashboardHeading />}
      <Grid item xs={12}>
        <Card className={classes.filterContainer}>
          <Grid container item spacing={2} xs={12}>
            <Grid item xs={12} lg={enableArchiveSearch ? (canSeeEstimatorSelect ? 4 : 6) : 6}>
              <DateRangePicker
                onConfirm={(startDate, endDate, selectedPresetKey) => {
                  if (!startDate && !endDate) {
                    // if no dates are selected, clear the date range
                    setDashboardState((prev) => ({
                      ...prev,
                      rangePresetKey: selectedPresetKey,
                      quoteDateMin: moment(0).startOf('day').unix() * 1000,
                      quoteDateMax: moment().endOf('day').unix() * 1000
                    }))
                  } else {
                    setDashboardState((prev) => ({
                      ...prev,
                      rangePresetKey: selectedPresetKey,
                      quoteDateMin: Math.max(startDate ? startDate.startOf('day').unix() * 1000 : 0, 0),
                      quoteDateMax: endDate ? endDate.endOf('day').unix() * 1000 : moment().endOf('day').unix() * 1000
                    }))
                  }
                }}
                inputProps={{ spacing: 2 }}
                startDate={moment(quoteDateMin)}
                endDate={moment(quoteDateMax)}
                disabled={!online}
              />
            </Grid>
            <Grid item xs={12} lg={!enableArchiveSearch && !canSeeEstimatorSelect && !isCorporateDashboard ? 6 : 3}>
              {isCorporate && !isMasquerading ? (
                <CorporateQuoteTypeSelect
                  className={classes.dropdownSelect}
                  placeholder={{
                    selectPlaceholder: `Any ${objectLabels.quote.value.toLowerCase()} type`,
                    plural: `Types`
                  }}
                  searchable
                  autoApply={false}
                  variant="multi"
                  hideAll
                  showCollapsibleChips
                  label={`${objectLabels.quote.value} Types`}
                  value={quoteTypes}
                  onChange={(val) =>
                    setDashboardState((prev) => ({
                      ...prev,
                      quoteTypes: val
                    }))
                  }
                />
              ) : (
                <QuoteTypeSelect
                  className={classes.dropdownSelect}
                  placeholder={{
                    selectPlaceholder: `Any ${objectLabels.quote.value.toLowerCase()} type`,
                    plural: `Types`
                  }}
                  searchable
                  autoApply={false}
                  variant="multi"
                  showCollapsibleChips
                  label={`${objectLabels.quote.value} Types`}
                  value={quoteTypes}
                  onChange={(val) =>
                    setDashboardState((prev) => ({
                      ...prev,
                      quoteTypes: val
                    }))
                  }
                />
              )}
            </Grid>
            {isCorporateDashboard && (
              <Grid item xs={12} lg={!enableArchiveSearch ? 3 : 3}>
                <DropdownSelect
                  className={classes.dropdownSelect}
                  variant="multi"
                  loading={locationsLoading}
                  label="Locations"
                  placeholder={{
                    selectPlaceholder: 'All Locations'
                  }}
                  options={locations}
                  value={selectedLocations}
                  onChange={(options) => setDashboardState((prev) => ({ ...prev, selectedLocations: options }))}
                  disabled={!online}
                />
              </Grid>
            )}
            <Grid item xs={12} lg={enableArchiveSearch ? (canSeeEstimatorSelect ? 5 : 3) : 3}>
              <Grid container spacing={2} alignItems={'flex-start'}>
                {canSeeEstimatorSelect && (
                  <Role permission="read:estimator-select">
                    <Grid item xs={12} sm={12} md={enableArchiveSearch ? 6 : 12}>
                      <EstimatorSelect
                        name="estimators"
                        label="Estimators"
                        className={classes.dropdownSelect}
                        fullWidth
                        variant="multi"
                        searchable
                        roles={['admin', 'sales']}
                        value={estimators.map(({ value }) => value)}
                        placeholder={{
                          selectPlaceholder: 'Any estimator',
                          plural: 'Estimators'
                        }}
                        userFields={['user_metadata.playerName', 'user_metadata.hasJoinedLeaderboard']}
                        disabled={!online}
                        onChange={(estimatorOptions: DashboardEstimator[]) => {
                          setDashboardState((prev) => ({
                            ...prev,
                            estimators: estimatorOptions
                          }))
                        }}
                        autoApply={false}
                        showCollapsibleChips
                        onFetched={(options: DashboardEstimator[]) => {
                          setAllEstimators(options)
                        }}
                      />
                    </Grid>
                  </Role>
                )}

                {enableArchiveSearch && (
                  <Grid item xs={12} sm={12} md={canSeeEstimatorSelect ? 6 : 12}>
                    <DropdownSelect
                      className={classes.dropdownSelect}
                      variant="single"
                      label="Archived"
                      placeholder={{
                        selectPlaceholder: 'Include Archived'
                      }}
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false }
                      ]}
                      value={
                        archived !== undefined
                          ? archived
                            ? { label: 'Yes', value: true }
                            : { label: 'No', value: false }
                          : null
                      }
                      onChange={(option) => setDashboardState((prev) => ({ ...prev, archived: option.value }))}
                      disabled={!online}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Card>
        <Grid container spacing={isXs ? 0 : 2}>
          <Grid item xs={12} sm={6}>
            {highestRole !== 'crew' && (
              <>
                <Hidden xlUp>
                  <StatCardGamified
                    showGamifiedCallouts={showGamifiedCallouts}
                    selectedEstimators={estimators}
                    allEstimators={allEstimators}
                    loading={loading}
                    primaryTitle="Sales"
                    primaryValue={formatCurrency({ options, value: reports?.wonQuotePrice, abbreviate: true })}
                    popoverAnchor={'right'}
                    statTitleLeft="Estimates Won"
                    statValueLeft={formatCurrency({
                      options,
                      value: reports?.wonQuotes,
                      abbreviate: false,
                      forceCurrency: '',
                      abbreviationPostfix: ''
                    })}
                    statTitleRight="Avg. Value"
                    statValueRight={formatCurrency({ options, value: reports?.averageWonPrice, abbreviate: true })}
                    popupContent={<QuotesPopover type={'won'} query={quoteSummaryQuery} />}
                    leaderboardRankData={leaderboardRank}
                    handleJoinClick={handleJoinClick}
                  />
                </Hidden>
                <Hidden lgDown>
                  <StatCardMainGamified
                    showGamifiedCallouts={showGamifiedCallouts}
                    corporateView={isCorporateDashboard}
                    selectedEstimators={estimators}
                    allEstimators={allEstimators}
                    loading={loading}
                    dollarsWon={reports.dollarsWon}
                    winRate={reports.winRate}
                    primaryTitle="Sales"
                    primaryValue={formatCurrency({ options, value: reports?.wonQuotePrice, abbreviate: true })}
                    popoverAnchor={'right'}
                    statTitleLeft="Estimates Won"
                    statValueLeft={formatCurrency({
                      options,
                      value: reports?.wonQuotes,
                      abbreviate: false,
                      forceCurrency: '',
                      abbreviationPostfix: ''
                    })}
                    statTitleRight="Avg. Value"
                    statValueRight={formatCurrency({ options, value: reports?.averageWonPrice, abbreviate: true })}
                    popupContent={<QuotesPopover type={'won'} query={quoteSummaryQuery} />}
                    leaderboardRankData={leaderboardRank}
                    handleJoinClick={handleJoinClick}
                  />
                </Hidden>
                <Hidden xlUp xsDown>
                  <Grid item xs={12} xl={6}>
                    <StatCardGamified
                      loading={loading}
                      primaryTitle={`Total ${objectLabels.quote.plural}`}
                      primaryValue={reports?.totalQuotes}
                      statTitleLeft="Worth"
                      statValueLeft={formatCurrency({ options, value: reports?.totalQuotePrice, abbreviate: true })}
                      statTitleRight="Avg. Value"
                      statValueRight={formatCurrency({ options, value: reports?.averageQuotePrice, abbreviate: true })}
                      variant="compact"
                      popupContent={<QuotesPopover type={'total'} query={quoteSummaryQuery} />}
                      handleJoinClick={handleJoinClick}
                    />
                  </Grid>
                </Hidden>
              </>
            )}
            {!isCorporateDashboard && (
              <Hidden xsDown>
                <Card className={classes.roundedCard}>
                  {crmFlag && !isCrew && (
                    <div className={classes.tabButtonContainer}>
                      {[
                        {
                          value: 'activity',
                          label: 'Activity',
                          active: currentActivityTab === 'activity'
                        },
                        {
                          value: 'tasks',
                          label: 'Tasks',
                          active: currentActivityTab === 'tasks'
                        }
                      ].map((tab) => (
                        <Button
                          key={tab.value}
                          variant="contained"
                          onClick={() => setCurrentActivityTab(tab.value)}
                          size="small"
                          className={classNames({
                            [classes.tabButton]: true,
                            [classes.activeTabButton]: tab.active
                          })}
                        >
                          {tab.label}
                        </Button>
                      ))}
                    </div>
                  )}
                  {currentActivityTab === 'activity' ? (
                    <DashboardActivity
                      query={activityQuery}
                      onUpdate={() => {
                        lastUpdated.current = Date.now()
                      }}
                      title={crmFlag && !isCrew ? '' : 'Activity'}
                    />
                  ) : (
                    <TaskDocList
                      dashboard
                      query={getTaskQuery({ estimators, user_id: isSales ? user.user_id : null })}
                      limit={10}
                    />
                  )}
                </Card>
              </Hidden>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {highestRole !== 'crew' && (
              <>
                <Grid direction="row" container spacing={!isLg ? 2 : 0}>
                  <Hidden xlUp>
                    <StatCardProgressGamified
                      showGamifiedCallouts={showGamifiedCallouts}
                      selectedEstimators={estimators}
                      allEstimators={allEstimators}
                      loading={loading}
                      primaryTitle={'Win Rate'}
                      primaryValue={reports.winRate}
                      secondaryTitle={'Dollars Won'}
                      secondaryValue={reports.dollarsWon}
                      leaderboardRankData={leaderboardRank}
                      handleJoinClick={handleJoinClick}
                    />
                  </Hidden>
                  <Hidden only={['sm', 'md', 'lg']}>
                    <Grid item xs={12} xl={6}>
                      <StatCardGamified
                        loading={loading}
                        primaryTitle={`Total ${objectLabels.quote.plural}`}
                        primaryValue={reports?.totalQuotes}
                        statTitleLeft="Worth"
                        statValueLeft={formatCurrency({ options, value: reports?.totalQuotePrice, abbreviate: true })}
                        statTitleRight="Avg. Value"
                        statValueRight={formatCurrency({
                          options,
                          value: reports?.averageQuotePrice,
                          abbreviate: true
                        })}
                        variant="compact"
                        popupContent={<QuotesPopover type={'total'} query={quoteSummaryQuery} />}
                        handleJoinClick={handleJoinClick}
                      />
                    </Grid>
                  </Hidden>
                  <Grid item xs={12} xl={6}>
                    <StatCardGamified
                      loading={loading}
                      primaryTitle={`Open ${objectLabels.quote.plural}`}
                      primaryValue={reports?.openQuotes}
                      statTitleLeft="Worth"
                      statValueLeft={formatCurrency({ options, value: reports?.openQuotePrice, abbreviate: true })}
                      statTitleRight="Avg. Value"
                      statValueRight={formatCurrency({ options, value: reports?.averageOpenPrice, abbreviate: true })}
                      variant="compact"
                      popupContent={<QuotesPopover type={'open'} query={quoteSummaryQuery} />}
                      handleJoinClick={handleJoinClick}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <StatCardProgressBar
                    closeRate={reports?.closeRate ?? 0}
                    label="Close Rate"
                    tooltipText="The percentage of estimates that are accepted or declined."
                    toolTipLink="https://help.paintscout.com/en/articles/10338915-what-does-close-rate-mean"
                    toolTopLinkText="Learn more"
                  />
                </Grid>
              </>
            )}
            {!isCorporateDashboard && (
              <>
                <Hidden xsDown>
                  <Card className={classes.roundedCard}>
                    {crmFlag && !isCrew && (
                      <div className={classes.tabButtonContainer}>
                        {[
                          {
                            value: 'chat',
                            label: 'Chat',
                            active: currentEventTab === 'chat'
                          },
                          {
                            value: 'events',
                            label: 'Events',
                            active: currentEventTab === 'events'
                          }
                        ].map((tab) => (
                          <Button
                            key={tab.value}
                            variant="contained"
                            onClick={() => setCurrentEventTab(tab.value)}
                            size="small"
                            className={classNames({
                              [classes.tabButton]: true,
                              [classes.activeTabButton]: tab.active
                            })}
                          >
                            {tab.label}
                          </Button>
                        ))}
                      </div>
                    )}
                    {currentEventTab === 'chat' ? (
                      <DashboardActivity
                        query={chatQuery}
                        onUpdate={() => {
                          lastUpdated.current = Date.now()
                        }}
                        noResultsMessage="No messages found."
                        showContent={true}
                        ActivityComponent={ChatActivityComponent}
                        // keep unreadMessages count in sync with activities (they are different queries)
                        // onDataReceived={() => refetchMessages()}
                        showClear={messagesData?.unreadMessages?.length > 0}
                        onClear={readMessages}
                        title={crmFlag && !isCrew ? '' : 'Chat'}
                      />
                    ) : (
                      <EventDocList
                        perPage={10}
                        query={getEventQuery({
                          estimators,
                          user_id: isSales ? user.user_id : null,
                          dateMin: quoteDateMin,
                          productionFlag
                        })}
                      />
                    )}
                  </Card>
                </Hidden>
                <Hidden smUp>
                  <Card>
                    <div className={classes.tabButtonContainer}>
                      {[
                        {
                          value: 'activity',
                          label: 'Activity',
                          active: currentMobileTab === 'activity'
                        },
                        crmFlag && !isCrew
                          ? {
                              value: 'tasks',
                              label: 'Tasks',
                              active: currentMobileTab === 'tasks'
                            }
                          : null,
                        {
                          value: 'chat',
                          label: 'Chat',
                          active: currentMobileTab === 'chat'
                        },
                        crmFlag && !isCrew
                          ? {
                              value: 'events',
                              label: 'Events',
                              active: currentMobileTab === 'events'
                            }
                          : null
                      ]
                        .filter(Boolean)
                        .map((tab) => (
                          <Button
                            key={tab.value}
                            variant="contained"
                            onClick={() => setCurrentMobileTab(tab.value)}
                            size="small"
                            className={classNames({
                              [classes.tabButton]: true,
                              [classes.activeTabButton]: tab.active
                            })}
                          >
                            {tab.label}
                          </Button>
                        ))}
                    </div>
                    {currentMobileTab === 'chat' ? (
                      <DashboardActivity
                        query={chatQuery}
                        onUpdate={() => {
                          lastUpdated.current = Date.now()
                        }}
                        noResultsMessage="No messages found."
                        showContent={true}
                        ActivityComponent={ChatActivityComponent}
                        showClear={messagesData?.unreadMessages?.length > 0}
                        onClear={readMessages}
                      />
                    ) : currentMobileTab === 'events' ? (
                      <EventDocList
                        query={getEventQuery({
                          estimators,
                          user_id: isSales ? user.user_id : null,
                          dateMin: quoteDateMin,
                          productionFlag
                        })}
                      />
                    ) : currentMobileTab === 'tasks' ? (
                      <TaskDocList query={getTaskQuery({ estimators, user_id: isSales ? user.user_id : null })} />
                    ) : (
                      <DashboardActivity
                        query={activityQuery}
                        onUpdate={() => {
                          lastUpdated.current = Date.now()
                        }}
                      />
                    )}
                  </Card>
                </Hidden>
              </>
            )}
          </Grid>
          {isCorporateDashboard && (
            <Grid container item spacing={2} style={{ paddingTop: 0 }}>
              <Grid item xs={12} style={{ paddingTop: 0 }}>
                <Card>
                  <GoogleMapsComponent locations={rows} showTotals />
                </Card>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </BasePage>
  )

  function DashboardHeading({ welcome }: { welcome?: boolean }) {
    return (
      <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
        <Grid item xs={welcome ? 12 : 8} sm={9}>
          {welcome ? (
            <>
              <PageTitle title={'Welcome to PaintScout!'} />
              <Typography>
                To start a new {objectLabels.quote.value}, click the <strong>+New</strong> button on your right. If you
                need help at any point, click the chat button in the bottom right to connect with our team!
              </Typography>
            </>
          ) : (
            <PageTitle
              classes={{ pageTitle: classes.primaryTitle }}
              title={profile.firstName ? `Hello ${profile.firstName}` : 'Dashboard'}
            />
          )}
        </Grid>
        {!online && (
          <Grid item>
            <Typography variant="subtitle1" gutterBottom style={{ float: 'left' }}>
              You are offline - results are read-only and may be out of date
            </Typography>
          </Grid>
        )}
      </Grid>
    )
  }
}

function getQuoteSummaryQuery({
  quoteDateMin,
  quoteDateMax,
  estimators,
  quoteTypes,
  useAcceptedDate,
  archived,
  selectedLocations,
  isCorporate,
  isMasquerading
}: {
  quoteDateMin: number
  quoteDateMax: number
  estimators: DashboardEstimator[]
  quoteTypes: string[]
  useAcceptedDate: boolean
  archived: boolean
  selectedLocations: DropdownSelectOption[]
  isCorporate: boolean
  isMasquerading: boolean
}): string {
  return jsonToLucene({
    filters: [
      {
        type: 'or' as const,
        value: [
          {
            type: 'and' as const,
            value: [
              {
                id: 'quote_date',
                type: 'range' as const,
                value: [quoteDateMin, quoteDateMax] as [any, any]
              },
              useAcceptedDate
                ? {
                    type: 'and' as const,
                    value: [
                      {
                        id: 'status.value',
                        type: 'equal' as const,
                        not: true,
                        value: 'accepted'
                      },
                      {
                        id: 'status.value',
                        type: 'equal' as const,
                        not: true,
                        value: 'invoiced'
                      }
                    ]
                  }
                : null
            ].filter((item) => item)
          },
          useAcceptedDate
            ? {
                type: 'and' as const,
                value: [
                  {
                    type: 'or' as const,
                    value: [
                      {
                        id: 'status.history.accepted.timestamp',
                        type: 'range' as const,
                        value: [quoteDateMin, quoteDateMax] as [any, any]
                      },
                      {
                        id: 'dates.client.accepted',
                        type: 'range' as const,
                        value: [quoteDateMin, quoteDateMax] as [any, any]
                      }
                    ]
                  },
                  {
                    type: 'or' as const,
                    value: [
                      {
                        id: 'status.value',
                        type: 'equal' as const,
                        value: 'accepted'
                      },
                      {
                        id: 'status.value',
                        type: 'equal' as const,
                        value: 'invoiced'
                      }
                    ]
                  }
                ]
              }
            : null
        ].filter((item) => item)
      },
      !archived && {
        id: 'archived',
        type: 'equal' as const,
        value: archived
      },
      {
        id: 'trashed',
        type: 'equal' as const,
        not: true,
        value: true
      },
      {
        id: 'source.provider',
        type: 'equal' as const,
        not: true,
        value: 'pep-import'
      },
      ...(estimators.length
        ? [
            {
              type: 'expand' as const,
              value: estimators.map(({ value: estimatorId }) => removeAuth0Prefix(estimatorId)),
              fields: ['owner.id']
            }
          ]
        : []),
      ...(quoteTypes.length
        ? [
            {
              type: 'expand' as const,
              value: quoteTypes.map((type) => `${type}`),
              fields: ['int_ext']
            }
          ]
        : []),
      ...(selectedLocations?.length && isCorporate && !isMasquerading
        ? [
            {
              type: 'expand' as const,
              value: selectedLocations.map(({ value }) => value),
              fields: ['locationId']
            }
          ]
        : [])
    ]
  })
}

function getActivityQuery({
  estimators,
  quoteTypes,
  quoteDateMin,
  quoteDateMax
}: {
  estimators: DashboardEstimator[]
  quoteTypes: string[]
  quoteDateMin: number
  quoteDateMax: number
}): string {
  return jsonToLucene({
    filters: [
      {
        id: 'details.timestamp',
        type: 'range' as const,
        value: [quoteDateMin, quoteDateMax]
      },
      {
        id: 'details.activityType',
        type: 'in',
        not: true,
        value: [
          'edited',
          'owner-changed',
          'message',
          'event',
          'tags-changed',
          'follow-up-sent',
          'paid',
          'note',
          'file-added',
          'task-created',
          'task-completed',
          'task-assigned',
          'payment-received-sent',
          'deal-created',
          'deal-assigned',
          'event-created',
          'event-cancelled',
          'event-assigned',
          'event-email-sent',
          'deal-stage-changed'
        ]
      },

      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      ...(quoteTypes.length
        ? [
            {
              type: 'expand' as const,
              value: quoteTypes.map((type) => `${type}`),
              fields: ['details.quote.int_ext', 'details.invoice.int_ext']
            }
          ]
        : []),
      ...(estimators.length
        ? [
            {
              type: 'expand' as const,
              value: estimators.map(({ value: estimatorId }) => removeAuth0Prefix(estimatorId)),
              fields: ['details.owner.id', 'assignedUsers']
            }
          ]
        : [])
    ]
  })
}

function getTaskQuery({ estimators, user_id }: { estimators: DashboardEstimator[]; user_id?: string }): string {
  const baseFilters = [
    {
      id: 'trashed',
      type: 'equal' as const,
      not: true,
      value: true
    },
    {
      id: 'completed',
      type: 'equal' as const,
      not: true,
      value: true
    },
    {
      id: 'startDateTime',
      type: 'range' as const,
      value: [0, 'Infinity'] as [any, any]
    }
  ]
  if (user_id) {
    return jsonToLucene({
      filters: [
        ...baseFilters,
        {
          id: 'owner.id',
          type: 'equal' as const,
          value: removeAuth0Prefix(user_id)
        }
      ]
    })
  }
  return jsonToLucene({
    filters: [
      ...baseFilters,
      ...(estimators.length
        ? [
            {
              type: 'expand' as const,
              value: estimators.map(({ value: estimatorId }) => removeAuth0Prefix(estimatorId)),
              fields: ['owner.id']
            }
          ]
        : [
            {
              id: 'owner.id',
              type: 'exists' as const
            }
          ])
    ]
  })
}

function getEventQuery({
  estimators,
  user_id,
  dateMin,
  productionFlag
}: {
  estimators: DashboardEstimator[]
  user_id?: string
  dateMin: number
  productionFlag: boolean
}) {
  return jsonToLucene({
    filters: [
      {
        id: 'details.activityType',
        type: 'equal' as const,
        value: 'event'
      },
      {
        id: 'details.timestamp',
        type: 'range' as const,
        value: [dateMin, 'Infinity']
      },
      {
        id: 'details.canceled',
        type: 'equal',
        not: true,
        value: true
      },
      {
        id: 'details.trashed',
        type: 'equal',
        value: true,
        not: true
      },
      ...(estimators.length
        ? [
            // {
            //   type: 'or' as const,
            //   value: [
            //     ...estimators.map<Filter>(({ value: estimatorId }) => ({
            //       id: 'details.owner.id',
            //       type: 'equal' as const,
            //       value: removeAuth0Prefix(estimatorId)
            //     }))
            //   ]
            // }
            {
              type: 'expand' as const,
              value: estimators.map(({ value: estimatorId }) => removeAuth0Prefix(estimatorId)),
              fields: ['details.owner.id']
            }
          ]
        : []),
      ...(user_id
        ? [
            {
              id: 'details.owner.id',
              type: 'equal' as const,
              value: removeAuth0Prefix(user_id)
            }
          ]
        : []),
      ...(!productionFlag ? [{ id: 'details.production', type: 'equal' as const, value: true, not: true }] : [])
    ]
  })
}

function getChatQuery({
  estimators,
  quoteDateMax,
  quoteDateMin,
  quoteTypes
}: {
  estimators: DashboardEstimator[]
  quoteDateMin: number
  quoteDateMax: number
  quoteTypes: string[]
}) {
  return jsonToLucene({
    filters: [
      {
        id: 'details.timestamp',
        type: 'range' as const,
        value: [quoteDateMin, quoteDateMax]
      },
      {
        id: 'details.activityType',
        type: 'equal',
        value: 'message'
      },
      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      ...(quoteTypes.length
        ? [
            {
              type: 'expand' as const,
              value: quoteTypes.map((type) => `${type}`),
              fields: ['details.quote.int_ext', 'details.invoice.int_ext']
            }
          ]
        : []),
      ...(estimators.length
        ? [
            {
              type: 'expand' as const,
              value: estimators.map(({ value: estimatorId }) => removeAuth0Prefix(estimatorId)),
              fields: ['details.owner.id', 'assignedUsers']
            }
          ]
        : [])
    ]
  })
}

function QuotesPopover({ type, query }: { type: string; query: string }) {
  const classes = useStyles({})
  const [page, setPage] = React.useState(1)
  // can either be won, closed or open, need to edit the query accordingly
  // add no draft, no invoice by default
  // if won needs to be accepted or invoiced status.value
  // if closed need to be accepted, invocied or declined status.value
  // if open needs to NOT be accpted, invoiced or declined
  const updatedQuery = updateQuotesPopoverQuery({ type, query })

  const { loading, data } = useSearchQuotesQuery({ variables: { query: updatedQuery, limit: 100 } })

  const quotes = data?.searchQuotes?.quotes ?? []
  const totalRows = quotes?.length
  const perRow = 50

  return (
    <div className={classes.popover}>
      {loading && <Spinner />}
      {!loading && totalRows > 0 && (
        <QuoteTable
          preTaxValue
          dense
          quotes={quotes}
          totalRows={totalRows}
          page={page}
          rowsPerPage={perRow}
          pages={Math.ceil(totalRows / perRow)}
          onPageChange={(e) => {
            setPage(e)
          }}
        />
      )}
      {!loading && !totalRows && (
        <Typography variant="body2" style={{ padding: 8 }}>
          No {type} Quotes found
        </Typography>
      )}
    </div>
  )
}

export default Dashboard

function updateQuotesPopoverQuery({ type, query }: { type: string; query: string }): string {
  let updatedQuery = '(' + query + ') AND NOT ((is_invoice: true) OR (status.value:"draft"))'
  const status = {
    won: ['accepted', 'invoiced'],
    closed: ['accepted', 'invoiced', 'declined'],
    open: ['accepted', 'paid', 'invoiced', 'declined']
  }

  const generateStatusQuery = (statuses: string[]) =>
    statuses.map((status) => `(status.value:"${status}")`).join(' OR ')

  if (type === 'won') {
    updatedQuery += ` AND (${generateStatusQuery(status.won)})`
  } else if (type === 'closed') {
    updatedQuery += ` AND (${generateStatusQuery(status.closed)})`
  } else if (type === 'open') {
    updatedQuery += ` AND NOT (${generateStatusQuery(status.open)})`
  }

  return updatedQuery
}
